if (!customElements.get('klaviyo-notify-modal')) {
  class KlaviyoNotifyModal extends ModalDialog {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.successMessage = this.querySelector('.js-popup-success-text');
      this.emailField = this.form?.querySelector('input[type="email"]');
      this.variantTitleEl = this.querySelector('.js-klaviyo-variant-title');
      this.variantRow = this.form?.querySelector('.js-notify-variant-row');
      this.listIdInput = this.form?.querySelector('.js-klaviyo-preorder-restock-id');
      this.newsletterCheckbox = this.form?.querySelector('.js-newsletter-checkbox');
      this.newsletterRow = this.form?.querySelector('.js-newsletter-row');
      this.newsletterListId = this.form?.dataset.newsletterListId || '';
      this.productId = this.dataset.productId || this.id?.replace('Klaviyo-notify-modal-', '');

      // Text elements for dynamic mode switching
      this.titleEl = this.querySelector('.klaviyo-popup__title');
      this.subtitleEl = this.querySelector('.klaviyo-popup__subtitle');
      this.btnEl = this.form?.querySelector('button[type="submit"]');

      // Parse mode text config from JSON
      const configEl = this.querySelector('script[type="application/json"]');
      const config = configEl ? JSON.parse(configEl.textContent) : {};
      const modes = config.modes || {};

      // Mode configs: text from JSON, UI behavior hardcoded per mode
      this.modeConfigs = {
        'bis': {
          title: modes.bis?.title || '',
          subtitle: modes.bis?.subtitle || '',
          btnText: modes.bis?.btnText || '',
          showVariantRow: true,
          showNewsletterRow: true,
          newsletterChecked: true,
          showVariantTitle: false,
        },
        'preorder-restock': {
          title: modes['preorder-restock']?.title || '',
          subtitle: modes['preorder-restock']?.subtitle || '',
          btnText: modes['preorder-restock']?.btnText || '',
          showVariantRow: false,
          showNewsletterRow: false,
          newsletterChecked: false,
          showVariantTitle: true,
        },
      };
    }

    getSelectedOption() {
      if (!this.productId) return null;
      const productForm = document.querySelector(`form[id="product-form-${this.productId}"]`);
      if (!productForm) return null;
      const select = productForm.querySelector('product-select select');
      return select ? select.querySelector('option:checked') : null;
    }

    getMode(option) {
      if (!option || option.hasAttribute('disabled')) return 'bis';
      if (option.getAttribute('data-preorder-klaviyo-form-id')) return 'preorder-restock';
      return 'bis';
    }

    applyMode(mode, option) {
      this.mode = mode;
      const config = this.modeConfigs[mode] || this.modeConfigs['bis'];

      // Variant-specific data from the selected product option
      if (this.listIdInput) {
        this.listIdInput.value = option?.getAttribute('data-preorder-klaviyo-form-id') || '';
      }
      if (this.variantTitleEl) {
        this.variantTitleEl.textContent = config.showVariantTitle && option
          ? (option.getAttribute('data-variant-title') || option.textContent.trim())
          : '';
      }

      // Apply mode UI config
      this.variantRow?.classList.toggle('hidden', !config.showVariantRow);
      this.newsletterRow?.classList.toggle('hidden', !config.showNewsletterRow);
      if (this.newsletterCheckbox) this.newsletterCheckbox.checked = config.newsletterChecked;
      if (this.titleEl) this.titleEl.textContent = config.title || '';
      if (this.subtitleEl) this.subtitleEl.textContent = config.subtitle || '';
      if (this.btnEl) this.btnEl.textContent = config.btnText || '';
    }

    hide() {
      super.hide();
    }

    show(opener) {
      const option = this.getSelectedOption();
      this.applyMode(this.getMode(option), option);
      super.show(opener);
    }

    subscribeToList(email, listId, source = 'Newsletter') {
      const companyId = this.form?.dataset.companyId;
      if (!companyId || !listId) return Promise.resolve();
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            },
            custom_source: source
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      };
      return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { revision: '2024-06-15', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    submitBIS(email, variantId) {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };
      return fetch(this.form?.getAttribute('action'), {
        method: 'POST',
        headers: { revision: '2024-06-15', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    shouldSubscribeNewsletter() {
      return this.newsletterCheckbox?.checked && this.newsletterListId;
    }

    onSubmitForm(event) {
      event.preventDefault();
      const email = this.emailField.value;
      const newsletter = this.shouldSubscribeNewsletter();

      if (this.mode === 'preorder-restock' && this.listIdInput?.value.trim()) {
        const listId = this.listIdInput.value.trim();

        this.subscribeToList(email, listId, 'Preorder')
          .then((res) => {
            if (res?.ok || res?.status === 204) this.formSubmitted();
            else console.error('Preorder restock subscribe error', res?.status);
          })
          .catch((err) => console.error(err));
      } else {
        const variantInput = this.form?.querySelector('[name="variant"]');
        if (!variantInput) return;

        this.submitBIS(email, variantInput.value)
          .then((res) => {
            if (!res.ok) {
              throw new Error(`BIS error: ${res.status}`);
            }
            if (newsletter) {
              return this.subscribeToList(email, this.newsletterListId, 'Back in Stock');
            }
          })
          .then(() => this.formSubmitted())
          .catch((err) => console.error(err));
      }
    }

    formSubmitted() {
      this.successMessage?.classList.remove('hidden');
      setTimeout(() => {
        this.hide();
        this.successMessage?.classList.add('hidden');
        if (this.emailField) this.emailField.value = '';
      }, 4000);
    }

    connectedCallback() {
      super.connectedCallback?.();
      this.form?.addEventListener('submit', this.onSubmitForm.bind(this));
    }
  }
  customElements.define('klaviyo-notify-modal', KlaviyoNotifyModal);
}
